import { markRaw } from "vue";
import { CAPABILITIES } from "../helpers/roles";
import { useAuthStore } from "../stores/auth";
import { hubEntryPath } from "./guard";
import Logout from "../components/views/Authentication/Logout.vue";
import PortalShell from "../components/portal/PortalShell.vue";
import PortalHome from "../components/portal/PortalHome.vue";
import PortalSession from "../components/portal/PortalSession.vue";
import PortalSessions from "../components/portal/PortalSessions.vue";
import PortalSessionsRoom from "../components/portal/PortalSessionsRoom.vue";
import PortalCoverage from "../components/portal/PortalCoverage.vue";
import PortalPolicy from "../components/portal/PortalPolicy.vue";
import PortalLibrary from "../components/portal/PortalLibrary.vue";
import PortalLibraryItem from "../components/portal/PortalLibraryItem.vue";
import PortalDetails from "../components/portal/PortalDetails.vue";
import Component404 from "../components/views/UnavailablePages/Component404.vue";
import Forbidden from "../components/views/UnavailablePages/Forbidden.vue";
import MyHubs from "../components/views/MyHubs.vue";
import MyProfile from "../components/views/MyProfile.vue";
import IconAttendees from "./../components/icons/IconAttendees.vue";
import IconAttendeesFilled from "./../components/icons/IconAttendeesFilled.vue";
import Agenda from "./../components/views/Agenda.vue";
import RecordingTranscriptionList from "./../components/views/RecordingTranscription/SessionsList.vue";
import RecordingTranscriptionDetail from "./../components/views/RecordingTranscription/SessionDetail.vue";
import PolicyholderRecording from "./../components/views/SessionContent/PolicyholderRecording.vue";
import PolicyholderMessages from "./../components/views/PolicyholderMessages.vue";
import Register from "./../components/views/Authentication/Register/index.vue";
import GoogleCallback from "./../components/views/Authentication/GoogleCallback.vue";
import ResetPassword from "./../components/views/Authentication/ResetPassword.vue";
import ConfirmEmail from "./../components/views/Authentication/ConfirmEmail.vue";
import { holdConfirmToken } from "./../helpers/confirmEmailLink";
import Authentication from "./../components/views/Authentication/index.vue";
import EventDetails from "./../components/views/EventDetails.vue";
import Registration from "./../components/views/Registration.vue";
import ResourcesAndContent from "./../components/views/ResourcesAndContent/ResourcesAndContent.vue";
import Overview from "./../components/views/Overview/Overview.vue";
import Users from "./../components/views/Users.vue";
import PolicyholderRecord from "./../components/views/PolicyholderRecord.vue";
import PolicyholderInvitedRecord from "./../components/views/PolicyholderInvitedRecord.vue";
import PolicyholderImport from "./../components/views/PolicyholderImport.vue";
import PolicyLines from "./../components/views/PolicyLines.vue";
import Messages from "./../components/views/Messages.vue";
import Communications from "./../components/views/Communications/index.vue";

/**
 * `meta.navItem` is the id of the sidebar item a route belongs to, from
 * src/routes/navigation.js. It is what lights the sidebar and what names the
 * page in the top bar, and it replaces path-prefix matching entirely.
 *
 * Many routes map to one item, which is the point: /sessions/new is Sessions;
 * /recordings/12 is Recordings. A route with no `navItem`
 * highlights nothing — correct for /forbidden and for pages outside the shell.
 *
 * `meta.title` overrides the item's label in the top bar for a route that
 * belongs to a section but is not that section's main page. A page
 * needing a title only known after a fetch calls setPageTitle() instead.
 */
/**
 * Insights is not in this phase (see its item in navigation.js). Its addresses
 * stay, and say so on the page (InsightsSoon), so an old bookmark or a typed
 * /insights is told what is true rather than sent elsewhere with no word.
 * Activity, Sessions and Engagement are kept; the dashboard (Home.vue) and its
 * widgets were removed as unused (knip) and are in git history for when
 * Insights comes back.
 */
const InsightsSoon = () => import("./../components/views/InsightsSoon.vue");

const adminRoutes = [
    // The hub URL is the door, not a brochure (Hub site page — remove it, every
    // route goes to sign-in). Nothing renders here: signed out, the guard sends
    // you to this hub's sign-in page; signed in, into the portal or the hub
    // (routes/guard.js, signInDetour). Every sign-out, sign-up step and email
    // that lands on `/` therefore lands at that door.
    {
        path: `/`,
        component: { render: () => null },
        name: "Hub Door",
        meta: { hideNav: true },
    },
    {
        path: `/settings`,
        component: EventDetails,
        name: "Settings",
        meta: {
            auth: true,
            admin: true,
            navItem: "settings",
            capability: CAPABILITIES.MANAGE_HUB_CONTENT,
        },
    },
    {
        path: `/policy-lines`,
        component: PolicyLines,
        name: "Policy lines",
        meta: {
            auth: true,
            admin: true,
            navItem: "policy-lines",
            // Reading is open to every role. Creating and editing are the owner's
            // and hub staff's (`hub:manage_policy_lines` on the API); archiving
            // stays the owner's (`hub:archive_policy_lines`).
            brokerAccess: true,
        },
    },
    {
        path: `/registration`,
        component: Registration,
        name: "Registration",
        meta: {
            auth: true,
            admin: true,
            navItem: "registration",
            capability: CAPABILITIES.MANAGE_HUB_CONTENT,
        },
    },
    {
        path: `/sessions/new`,
        component: () => import("../components/views/Agenda/CreateSession.vue"),
        name: "Create Session",
        meta: {
            auth: true,
            admin: true,
            // The form lives under Sessions: the sidebar keeps that item lit
            // rather than going dark on a page reached from it.
            navItem: "sessions",
            // The owner, hub staff and brokers (Office Hours QA, AC4): a broker
            // creates Webinars and Video Conferences of their own, and edits
            // only those — the page turns away any other session they open.
            capability: CAPABILITIES.CONTRIBUTE_HUB_CONTENT,
        },
    },
    // Editing reuses the create page rather than the modal it replaced, so
    // creating and editing a session describe it the same way. `?scope=series`
    // is what separates "Edit this date" from "Edit the whole series".
    {
        path: `/sessions/:id(\\d+)/edit`,
        component: () => import("../components/views/Agenda/CreateSession.vue"),
        name: "Edit Session",
        meta: {
            auth: true,
            admin: true,
            navItem: "sessions",
            // The owner, hub staff and brokers (Office Hours QA, AC4): a broker
            // creates Webinars and Video Conferences of their own, and edits
            // only those — the page turns away any other session they open.
            capability: CAPABILITIES.CONTRIBUTE_HUB_CONTENT,
        },
    },
    {
        path: `/sessions`,
        component: Agenda,
        name: "Sessions",
        beforeEnter: (to) => (to.query.create === 'session' ? { name: 'Create Session' } : undefined),
        meta: {
            auth: true,
            admin: true,
            navItem: "sessions",
            brokerAccess: true,
        },
    },
    {
        path: `/content`,
        component: ResourcesAndContent,
        name: "Content",
        meta: {
            auth: true,
            admin: true,
            navItem: "content",
            brokerAccess: true,
        },
    },
    {
        path: `/recordings/:agendaEventId(\\d+)`,
        component: RecordingTranscriptionDetail,
        name: "Recording Detail",
        // No `title`: the page calls setPageTitle() with the session's own name
        // once it has loaded, and falls back to "Recordings" until then.
        meta: { auth: true, admin: true, navItem: "recordings", brokerAccess: true },
    },
    // The hub site page, its editor, its preview and its public agenda are no
    // longer served. Their code stays in Git; their addresses go to the door,
    // so an old bookmark or link neither renders them nor 404s.
    { path: `/builder`, redirect: '/' },
    { path: `/landing-preview`, redirect: '/' },
    { path: `/landing-agenda`, redirect: '/' },
    {
        path: `/overview`,
        component: Overview,
        name: "Overview",
        // brokerScope: this page reads and writes ?broker_id, so the filter
        // survives moving between it and Policyholders.
        meta: {
            auth: true,
            admin: true,
            navItem: "overview",
            brokerAccess: true,
            brokerScope: true,
        },
    },
    {
        path: `/policyholders`,
        component: Users,
        name: "Policyholders",
        meta: {
            auth: true,
            admin: true,
            // Brokers belong on this page, scoped to their own book by the
            // server. Every other admin route stays owner/hub-staff only.
            brokerAccess: true,
            parent: true,
            hideOnMinWindow: true,
            iconActive: markRaw(IconAttendeesFilled),
            icon: markRaw(IconAttendees),
            group: 2,
            navItem: "policyholders",
            brokerScope: true,
        },
    },
    {
        path: `/messages`,
        component: Messages,
        name: "Messages",
        meta: {
            auth: true,
            admin: true,
            // A broker's own book of conversations is the point of the page,
            // so they belong here as much as an owner does — confined by the
            // server, as everywhere else.
            brokerAccess: true,
            brokerScope: true,
            hideOnMinWindow: true,
            group: 2,
            navItem: "messages",
        },
    },
    {
        // Brokers are admitted so the page itself can send them back to the
        // list: an import is owner and hub staff only.
        path: `/policyholders/import`,
        component: PolicyholderImport,
        name: "Import policyholders",
        meta: {
            auth: true,
            admin: true,
            brokerAccess: true,
            brokerScope: true,
            hideOnMinWindow: true,
            navItem: "policyholders",
        },
    },
    {
        path: `/policyholders/invited/:id(\\d+)`,
        component: PolicyholderInvitedRecord,
        name: "Invited policyholder record",
        meta: {
            auth: true,
            admin: true,
            brokerAccess: true,
            brokerScope: true,
            hideOnMinWindow: true,
            navItem: "policyholders",
        },
    },
    {
        // The record is its own page, not a dialog: it holds policies, broker,
        // resources and registration answers, each edited in place.
        path: `/policyholders/:id`,
        component: PolicyholderRecord,
        name: "Policyholder record",
        meta: {
            auth: true,
            admin: true,
            brokerAccess: true,
            brokerScope: true,
            hideOnMinWindow: true,
            navItem: "policyholders",
        },
    },
    {
        path: `/communications`,
        component: Communications,
        name: "Communications",
        meta: {
            auth: true,
            admin: true,
            navItem: "communications",
            // A broker reaches the read surface now: segments, templates and
            // sequences to work inside, their own history, and sending. Each
            // of those routes narrows per viewer, and everything that defines
            // a segment or runs a sequence is still tenant-admin — the tab
            // renders no control for what they cannot do, so nothing offers a
            // Save that would fail.
            brokerAccess: true,
        },
    },
    {
        path: `/recordings`,
        component: RecordingTranscriptionList,
        name: "Recordings",
        meta: {
            auth: true,
            admin: true,
            navItem: "recordings",
            brokerAccess: true,
        },
    },
    {
        path: `/insights`,
        name: "Insights",
        component: InsightsSoon,
        meta: {
            auth: true,
            admin: true,
            navItem: "insights",
            brokerAccess: true,
        },
    },
    {
        // Only a redirect target: links still point here, and the guard sends
        // policyholders to /hub/details and staff to /my-profile. It renders nothing.
        path: `/profile`,
        component: { render: () => null },
        name: "Profile",
        meta: { auth: true, hideNav: true },
    },
    // The Insights sub-pages deliberately carry no `title`: they already name
    // themselves in their own sub-nav and section heading, so the top bar
    // shows the section — which keeps it matching the lit sidebar item.
    {
        path: `/insights/activity`,
        component: InsightsSoon,
        name: "Activity",
        meta: { auth: true, admin: true, navItem: "insights", brokerAccess: true },
    },
    {
        // Named "Insights Sessions", not "Sessions": that name now belongs to
        // the agenda page, and two routes cannot share one name.
        path: `/insights/sessions`,
        component: InsightsSoon,
        name: "Insights Sessions",
        meta: { auth: true, admin: true, navItem: "insights", brokerAccess: true },
    },
    {
        path: `/insights/engagement`,
        component: InsightsSoon,
        name: "Engagement",
        meta: { auth: true, admin: true, navItem: "insights", brokerAccess: true },
    },
    {
        // Where the guard sends someone who reached a page their role does not
        // allow. Deliberately carries no `capability`, and renders INSIDE the
        // shell so they keep their navigation instead of landing somewhere that
        // looks like a crash.
        path: `/forbidden`,
        component: Forbidden,
        name: "Not available",
        meta: { auth: true, admin: true, title: "Not available", brokerAccess: true },
    },
    {
        path: `/my-hubs`,
        component: MyHubs,
        name: "My Hubs",
        meta: { auth: true, admin: true, title: "My Hubs", brokerAccess: true },
    },
    {
        path: `/my-profile`,
        component: MyProfile,
        name: "My Profile",
        meta: { auth: true, admin: true, title: "Profile", brokerAccess: true },
    },
    {
        path: "/logout",
        component: Logout,
        name: "Logout",
        meta: { hideNav: true },
    },
    {
        path: `/login`,
        component: Authentication,
        name: "Auth Login",
        children: [
            {
                path: "",
                component: Register,
                name: "Login",
            },
        ],
        meta: { hideNav: true },
    },
    {
        path: `/register`,
        component: Authentication,
        name: "Auth Register",
        children: [
            {
                path: "",
                component: Register,
                name: "Register",
            },
        ],
        meta: { hideNav: true },
    },
    {
        path: `/auth/google/callback`,
        component: Authentication,
        name: "Google Callback",
        children: [
            {
                path: "",
                component: GoogleCallback,
                name: "GoogleCallback",
            },
        ],
        meta: { hideNav: true },
    },
    {
        path: `/reset-password`,
        component: Authentication,
        name: "Reset Password",
        children: [
            {
                path: "",
                component: ResetPassword,
                name: "ResetPassword",
            },
        ],
        meta: { hideNav: true },
    },
    // The link SSO mails to a new address (XN-4561). Public, like the reset:
    // whoever clicks may be signed out, or signed in on this device, and the
    // page only confirms — no `auth`, so the guard sends nobody elsewhere.
    {
        path: `/confirm-email`,
        component: Authentication,
        name: "Confirm Email",
        // The token leaves the address bar before the layout asks the server for anything.
        beforeEnter: holdConfirmToken,
        children: [
            {
                path: "",
                component: ConfirmEmail,
                name: "ConfirmEmail",
            },
        ],
        meta: { hideNav: true },
    },
    // The Client Portal (XN-4552): a policyholder's whole side of the hub,
    // every page inside one shell. Pages not rebuilt yet are today's, wrapped;
    // each Client Portal story replaces its own child.
    {
        path: `/hub`,
        component: PortalShell,
        meta: { auth: true, hideNav: true },
        children: [
            { path: '', component: PortalHome, name: 'Enter Hub', meta: { title: 'Home', policyholderOnly: true } },
            { path: 'coverage', component: PortalCoverage, name: 'Portal Coverage', meta: { title: 'My coverage', policyholderOnly: true } },
            { path: `coverage/:id(\\d+)`, component: PortalPolicy, name: 'Portal Policy', meta: { title: 'My coverage', policyholderOnly: true } },
            // The room is hosted around every Sessions page, so a call survives
            // moving between them (XN-4558).
            {
                path: 'sessions',
                component: PortalSessionsRoom,
                children: [
                    { path: '', component: PortalSessions, name: 'Portal Sessions', meta: { title: 'Sessions' } },
                    { path: ':id(\\d+)', component: PortalSession, name: 'Portal Session', meta: { title: 'Sessions' } },
                    // The accept screen for a speaker invitation. Reached three
                    // ways — the emailed link (which adds ?i=<token>), the
                    // sessions list, and the session page — and identical
                    // whichever way you arrive, so deleting the email costs
                    // nothing.
                    {
                        path: ':id(\\d+)/speaking',
                        component: () => import('../components/views/SessionSpeaking/SpeakingInvitation.vue'),
                        name: 'Speaker Invitation',
                        meta: { title: 'Sessions' },
                    },
                ],
            },
            { path: 'library', component: PortalLibrary, name: 'Portal Library', meta: { title: 'Library', policyholderOnly: true } },
            { path: `library/:id(\\d+)`, component: PortalLibraryItem, name: 'Portal Library Item', meta: { title: 'Library', policyholderOnly: true } },
            { path: 'messages', component: PolicyholderMessages, name: 'My Messages', meta: { title: 'Messages', policyholderOnly: true } },
            { path: 'recordings', redirect: { path: '/hub/sessions', query: { tab: 'recordings' } } },
            { path: `recordings/:agendaEventId(\\d+)`, component: PolicyholderRecording, name: 'Watch Recording', meta: { title: 'Recording' } },
            { path: 'details', component: PortalDetails, name: 'Portal Details', meta: { title: 'My details', policyholderOnly: true } },
        ],
    },

    // The one page in the app a person with no account is meant to open: an
    // outside guest's one-time speaker link. No `auth`, because the token in
    // the URL is all there is.
    {
        path: `/speaker/:token`,
        component: () => import('../components/views/SessionSpeaking/GuestSpeakerLanding.vue'),
        name: 'Guest Speaker',
        meta: { hideNav: true },
    },

    // Every path this app used to answer on, kept as a redirect so bookmarks,
    // emails and anything linking in from outside still land somewhere real.
    { path: `/members-administration`, redirect: `/policyholders` },
    { path: `/attendee-administration`, redirect: `/policyholders` },
    { path: `/setup`, redirect: () => hubEntryPath(useAuthStore()) },
    { path: `/setup/hub-basics`, redirect: () => hubEntryPath(useAuthStore()) },
    { path: `/setup/registration`, redirect: `/registration` },
    { path: `/setup/agenda`, redirect: `/sessions` },
    { path: `/setup/resource-content`, redirect: `/content` },
    { path: `/setup/recording-transcription`, redirect: `/recordings` },
    {
        path: `/setup/recording-transcription/:agendaEventId(\\d+)`,
        redirect: (to) => `/recordings/${to.params.agendaEventId}`,
    },
    { path: `/analytics`, redirect: `/insights` },
    { path: `/analytics/activity`, redirect: `/insights/activity` },
    { path: `/analytics/sessions`, redirect: `/insights/sessions` },
    { path: `/analytics/engagement`, redirect: `/insights/engagement` },

    {
        path: `/:pathMatch(.*)*`,
        component: Component404,
        name: "404 Page",
        meta: { hideNav: true },
    },
];

export default adminRoutes;
